import { useEffect, useState, type ReactNode } from 'react'

import { tryAutoRefresh } from '../auth'
import { useAuthStore } from '../stores/auth.store'

import ComingSoonPage from './ComingSoonPage'

type ComingSoonApp = 'academy' | 'prompts' | 'forge' | 'madsaas'

type ComingSoonGateProps = {
  app: ComingSoonApp
  hubUrl?: string
  /**
   * Active le mode "Bientôt disponible". Quand false, le gate est transparent
   * et rend directement le site (équivaut à VITE_COMING_SOON !== 'true').
   */
  enabled?: boolean
  children: ReactNode
}

/**
 * Gate "Bientôt disponible" avec bypass admin.
 *
 * Objectif : pouvoir tester le vrai site en prod pendant que le public voit
 * encore la page "Bientôt disponible".
 *
 * Comportement :
 * 1. Le check de session (refresh cookie partagé .neuroforge.link) est lancé
 *    AVANT tout affichage. Tant que l'auth n'est pas résolue, on n'affiche ni
 *    le site ni la page coming soon (anti-flash).
 * 2. Si l'utilisateur connecté est ADMIN -> accès au site réel.
 * 3. Sinon -> page "Bientôt disponible".
 *
 * NB : ne fonctionne que sur les apps qui partagent le cookie .neuroforge.link
 * (academy, prompts, forge, hub). madsaas.com est un domaine distinct sans
 * session NeuroForge : le bypass ne s'y applique pas.
 */
export default function ComingSoonGate({
  app,
  hubUrl = '/',
  enabled = true,
  children,
}: ComingSoonGateProps) {
  const { isAuthenticated, user } = useAuthStore()
  const [checking, setChecking] = useState(enabled && !isAuthenticated)

  useEffect(() => {
    if (!enabled) return
    if (isAuthenticated) {
      setChecking(false)
      return
    }

    let cancelled = false
    setChecking(true)
    // Idempotent : tryAutoRefresh a un mutex global (déduplique l'appel de main.tsx).
    tryAutoRefresh().finally(() => {
      if (!cancelled) setChecking(false)
    })
    return () => {
      cancelled = true
    }
  }, [enabled, isAuthenticated])

  // Mode coming soon désactivé -> site normal.
  if (!enabled) return <>{children}</>

  // Auth en cours de résolution -> ni site ni coming soon (anti-flash).
  if (checking) {
    return <main className="min-h-screen bg-[var(--bg)]" aria-busy="true" />
  }

  // Admin connecté -> accès au site réel pour tester avant l'ouverture.
  if (user?.role === 'ADMIN') return <>{children}</>

  // Public -> page "Bientôt disponible".
  return <ComingSoonPage app={app} hubUrl={hubUrl} />
}
